// React and External Libraries
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';

// APIs and Types
import {
  CreateSurveyDto,
  SurveyCreationDraftTableDto,
  SurveyTableDto,
} from '../../../typescript-axios';

// Contexts
import { MarsinetContext } from '../../../context/marsinetProvider';
import { useToast } from '../../../context/ToastProvider';

// Components
import { configureAxios } from '../../../utils/utils';
import { apiServices } from '../../../services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import ModalAddSurveys from './components/modal-add-surveys/modal-add-surveys';
import TableSurveysComponent from './components/table-surveys-component/table-surveys-component';
import TableDraftSurveysComponent from './components/table-drafts-surveys-component/table-drafts-surveys-component';
import { AvailableResourceActions, can } from '@marsinet/commons';

const Surveys: React.FC = () => {
  const { showToast: show } = useToast();
  const { t } = useTranslation();
  const { access_token, role, setRole, setAdmin, setError } =
    useContext(MarsinetContext);
  const [visibleCreateSurvey, setVisibleCreateSurvey] = useState(false);
  const [updateChanges, setUpdateChanges] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [surveys, setSurveys] = useState<SurveyTableDto[]>([]);
  const [drafts, setDrafts] = useState<SurveyCreationDraftTableDto[]>([]);
  const [selectedDraft, setSelectedDraft] =
    useState<SurveyCreationDraftTableDto | null>(null);

  const navigate = useNavigate();

  const canViewPage = useMemo(
    () => !!role && can(role, AvailableResourceActions.surveys.read_section),
    [role]
  );

  useEffect(() => {
    if (!role) return;
    if (!canViewPage) {
      navigate('/authenticated');
    }
  }, [role, canViewPage, navigate]);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const response =
          await apiServices.myAccountApi.myAccountControllerGetData();
        const data = response.data;

        setRole(data.organization?.role);
        setAdmin(data.can_edit);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
            error_page: window.location.pathname,
            error_stack: error.stack || 'No stack trace',
            timestamp: new Date().toISOString(),
            browser_info: navigator.userAgent,
          });
        }
      }
    };
    if (!access_token) return;
    fetchAccountData();
  }, [access_token]);

  useEffect(() => {
    if (!access_token) return;
    if (!role) return;
    if (!canViewPage) return;
    configureAxios(access_token);
    fetchAvailableSurveys();
    fetchDrafts();
  }, [access_token, updateChanges, role, canViewPage]);

  const createSurveyFunction = async (
    values: CreateSurveyDto,
    draftId?: string
  ) => {
    try {
      await apiServices.surveyApi.surveyControllerCreateSurvey(values, draftId);
      setUpdateChanges(!updateChanges); // refetches surveys AND drafts
      show('success', t('messages.success'), t('messages.surveys.create_survey'));
    } catch (error) {
      show('error', t('messages.error'), t('messages.surveys.survey_created_error'));
    }
  };

  const fetchDrafts = async () => {
    try {
      const response =
        await apiServices.surveyApi.surveyControllerGetSurveyCreationDrafts();
      setDrafts(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        setError(true);
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
          error_page: window.location.pathname,
          error_stack: error.stack || 'No stack trace',
          timestamp: new Date().toISOString(),
          browser_info: navigator.userAgent,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSurveys = async () => {
    try {
      setLoading(true);
      const response =
        await apiServices.surveyApi.surveyControllerGetAllSurveys();
      setSurveys(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        setError(true);
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
          error_page: window.location.pathname,
          error_stack: error.stack || 'No stack trace',
          timestamp: new Date().toISOString(),
          browser_info: navigator.userAgent,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const goToSurvey = async (row: SurveyTableDto) => {
    if (!row) return;
    navigate(`/authenticated/surveys/${row.id}`);
  };

  // Only hide the page once we know the role and it isn't allowed.
  if (role && !canViewPage) return null;

  const handleSurveyDeleted = (id: string) => {
    setSurveys((prev) => prev.filter((d) => d.id !== id));
  };

  const handleSurveyStatusChanged = (id: string, isActive: boolean) => {
    setSurveys((prev) =>
      prev.map((survey) =>
        survey.id === id ? { ...survey, is_active: isActive } : survey
      )
    );
  };

  const handleDraftSaved = () => {
    fetchDrafts();
    setSelectedDraft(null);
  };

  const handleEditDraft = (draft: SurveyCreationDraftTableDto) => {
    setSelectedDraft(draft);
    setVisibleCreateSurvey(true);
  };

  const handleDeleteDraft = async (draft: SurveyCreationDraftTableDto) => {
    try {
      await apiServices.surveyApi.surveyControllerDeleteSurveyCreationDraft(
        draft.id
      );
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    } catch (error) {
      show('error', t('messages.error'), t('messages.surveys.draft_deleted_error'));
    }
  };

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      <div className="col-12">
        <ConfirmDialog />
        <div className="surface-card p-3 shadow-3 border-round-xl flex justify-content-between align-items-center mb-5">
          <div className="font-medium text-lg sm:text-xl md:text-2xl text-900">
            {t('pages.surveys.title')}
          </div>
          <Button
            icon="pi pi-plus"
            label={t('pages.surveys.add')}
            onClick={() => setVisibleCreateSurvey(true)}
            severity="secondary"
            aria-label="Add"
            className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
            tooltipOptions={{
              position: 'bottom',
              mouseTrack: true,
              mouseTrackTop: 20,
            }}
          />
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="col-12 grid mx-0 mb-3">
          <div className="surface-card border-round-xl shadow-3 py-2 px-3 col-12">
            <div className="font-medium text-2xl text-900 mb-4 mt-3 mr-3">
              {t('pages.surveys.drafts_title')}
            </div>
            <TableDraftSurveysComponent
              drafts={drafts}
              loading={loading}
              onEditDraft={handleEditDraft}
              onDeleteDraft={handleDeleteDraft}
            />
          </div>
        </div>
      )}
      <div className="col-12 grid mx-0">
        <div className="surface-card border-round-xl shadow-3 py-2 px-3 col-12">
          <TableSurveysComponent
            filtersSurveys={surveys}
            goToSurvey={goToSurvey}
            loading={loading}
            onSurveyDeleted={handleSurveyDeleted}
            onSurveyStatusChanged={handleSurveyStatusChanged}
          />
          <ModalAddSurveys
            visible={visibleCreateSurvey}
            setVisible={(v) => {
              setVisibleCreateSurvey(v);
              if (!v) setSelectedDraft(null);
            }}
            createSurveyFunction={createSurveyFunction}
            draft={selectedDraft}
            onDraftSaved={handleDraftSaved}
          />
        </div>
      </div>
    </div>
  );
};

export default Surveys;
